









 
import { useEffect, useState } from "react";
import {
  getSubtasksApi,
  addSubtaskApi,
  toggleSubtaskApi,
  addSubtaskDependencyApi,
  updateSubtaskApi,
  deleteSubtaskApi
} from "../../app/api";
import { Check, GitBranch, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { formatDisplayDate, getDateInputValue, getTodayDateInputValue } from "../../utils/dateFormat";
 
export default function TaskSubtasks({ taskId, onProgressChange, onSubtaskUpdate }) {
 
 
  const [startDate, setStartDate] = useState("");
  const [subtasks, setSubtasks] = useState([]);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [openMenu, setOpenMenu] = useState(null);
 
  const total = subtasks.length;
  const completed = subtasks.filter((s) => s.is_done).length;
  const [showModal, setShowModal] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [editSubtask, setEditSubtask] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDate, setEditDate] = useState("");

  const handleUpdateSubtask = async () => {
    if (!editSubtask) return;
  
    try {
      await updateSubtaskApi(editSubtask.id, {
        title: editTitle,
        start_date: editDate,
      });
  
      setEditSubtask(null);
      setEditTitle("");
      setEditDate("");
  
      fetchSubtasks();
  
      // ✅ refresh parent
      onSubtaskUpdate?.(taskId);
  
    } catch (err) {
      console.error("Update error:", err);
    }
  };

  const handleDeleteSubtask = async (subtask) => {
    if (!window.confirm(`Delete "${subtask.title}"?`)) return;
    try {
      await deleteSubtaskApi(subtask.id);
      onSubtaskUpdate?.(taskId);
      fetchSubtasks();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };
 
  const handleSubmitDependency = async () => {
    if (!selectedParent || selectedChildren.length === 0) {
      alert("Select parent and children tasks");
      return;
    }
 
    try {
      for (let childId of selectedChildren) {
        if (childId !== Number(selectedParent)) {
          await addSubtaskDependencyApi(childId, {
            depends_on_subtask_id: selectedParent,
          });
        }
      }
 
      const refreshed = await getSubtasksApi(taskId);
      setSubtasks(refreshed.data || []);
 
      setShowModal(false);
      setSelectedChildren([]);
      setSelectedParent(null);
 
    } catch (err) {
      console.error("Dependency error", err);
    }
  };
 
 
  /* ================= FETCH SUBTASKS ================= */
  const fetchSubtasks = async () => {
    if (!taskId) return;
 
    setLoading(true);
    try {
      const res = await getSubtasksApi(taskId);
      // const list = (res.data || []).sort((a, b) => a.id - b.id);
 
      const today = getTodayDateInputValue();
 
const list = (res.data || []).sort((a, b) => {
  const aDateTime = new Date(a.start_date || a.created_at);
  const bDateTime = new Date(b.start_date || b.created_at);
 
  // ✅ STRICTLY BY DATE (DESCENDING)
  return bDateTime - aDateTime;
});
 
      setSubtasks(list);
 
      onProgressChange?.({
        total: list.length,
        completed: list.filter((s) => s.is_done).length,
      });
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };
 
 
 
 
  useEffect(() => {
    fetchSubtasks();
  }, [taskId]);
 
  /* ================= ADD SUBTASK ================= */
  const addSubtask = async () => {
    if (!title.trim()) return;
 
    await addSubtaskApi(taskId, { title, start_date: startDate, });
 
// ✅ ADD THIS LINE
onSubtaskUpdate?.(taskId);
 
 
    setTitle("");
    setStartDate(""); // reset
 
    fetchSubtasks();
  };
 
  /* ================= TOGGLE SUBTASK ================= */
  const toggleSubtask = async (subtask) => {
    try {
      await toggleSubtaskApi(subtask.id, {
        is_done: !subtask.is_done,
      });
  // ✅ ADD THIS LINE
  onSubtaskUpdate?.(taskId);
      fetchSubtasks();
    } catch (err) {
      console.error("Toggle error:", err);
    }
  };
 
  /* ================= ADD DEPENDENCY ================= */
  const addDependency = async (subtaskId, dependsOnId) => {
    try {
      await addSubtaskDependencyApi(subtaskId, {
        depends_on_subtask_id: dependsOnId,
      });
 
      fetchSubtasks();
      setOpenMenu(null);
    } catch (err) {
      console.error("Dependency error:", err);
    }
  };
 
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-400 shadow-sm">
        Loading subtasks…
      </div>
    );
  }
 
 
 
  const groupedSubtasks = subtasks.reduce((acc, sub) => {
  const date =
    (sub.start_date || sub.created_at)?.split(" ")[0];
 
  if (!acc[date]) {
    acc[date] = [];
  }
 
  acc[date].push(sub);
  return acc;
}, {});
 
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-black uppercase tracking-[0.14em] text-slate-700">Subtasks</h3>
          <p className="mt-1 text-xs font-medium text-slate-400">Break this card into smaller steps</p>
        </div>
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-600">
          {completed}/{total}
        </span>
      </div>
 
      {/* ===== PROGRESS BAR ===== */}
      {total > 0 && (
        <div className="mb-4">
          <div className="mb-1 flex justify-between text-xs font-bold text-slate-500">
            <span>{completed} / {total} done</span>
            <span>{Math.round((completed / total) * 100)}%</span>
          </div>
 
          <div className="h-2 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 transition-all duration-300"
              style={{ width: `${(completed / total) * 100}%` }}
            />
          </div>
 
          {completed === total && (
            <div className="mt-3 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-xs font-semibold text-green-700">
              All subtasks completed. You can move this task forward.
            </div>
          )}
        </div>
      )}
 
      {/* ===== SUBTASK LIST ===== */}
      <div className="flex flex-col gap-2">
        {subtasks.map((sub) => {
          const dependency = subtasks.find(
            (s) => s.id === sub.depends_on_subtask_id
          );
 
          const isLocked =
            dependency && !dependency.is_done;
 
          return (
            <div
              key={sub.id}
              className={`relative flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-sm transition ${
                sub.is_done
                  ? "border-emerald-100 bg-emerald-50/70"
                  : isLocked
                  ? "border-orange-100 bg-orange-50/70"
                  : "border-slate-200 bg-slate-50 hover:bg-white"
              }`}
            >
              {/* LEFT SIDE */}
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  disabled={isLocked}
                  onClick={() => !isLocked && toggleSubtask(sub)}
                  className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border transition ${
                    sub.is_done
                      ? "border-emerald-400 bg-emerald-500 text-white"
                      : isLocked
                      ? "cursor-not-allowed border-orange-200 bg-orange-100 text-orange-300"
                      : "border-slate-300 bg-white text-transparent hover:border-blue-400 hover:text-blue-500"
                  }`}
                  aria-label={sub.is_done ? "Mark incomplete" : "Mark complete"}
                >
                  <Check size={14} strokeWidth={3} />
                </button>
 
                {/* <span
                  className={
                    sub.is_done
                      ? "line-through text-gray-400"
                      : isLocked
                      ? "text-gray-400"
                      : ""
                  }
                >
                  {sub.title}
                </span> */}
 
 
<div className="flex min-w-0 flex-col">
  <span
    className={`truncate font-bold ${
      sub.is_done
        ? "line-through text-slate-400"
        : isLocked
        ? "text-orange-500"
        : "text-slate-800"
    }`}
  >
    {sub.title}
  </span>
 
  {/* ✅ START DATE DISPLAY */}
  <span className="text-[11px] font-semibold text-slate-400">
  Start: {formatDisplayDate(sub.start_date || sub.created_at, "--")}
</span>
</div>
 
                {isLocked && (
                  <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-orange-700">
                    Locked
                  </span>
                )}
              </div>
 
              {/* RIGHT SIDE - THREE DOT MENU */}
              <div className="relative">
               
 
{/* <button onClick={() => setShowModal(true)}>
  ⋮
</button> */}


<div className="flex items-center gap-2">
  {/* ✏️ EDIT ICON */}
  <button
    onClick={() => {
      setEditSubtask(sub);
      setEditTitle(sub.title);
      setEditDate(getDateInputValue(sub.start_date));
    }}
    className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 transition hover:bg-blue-50 hover:text-blue-600"
    title="Edit subtask"
  >
    <Pencil size={14} />
  </button>

  {/* 🗑️ DELETE ICON */}
  <button
    onClick={() => handleDeleteSubtask(sub)}
    className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-600"
    title="Delete subtask"
  >
    <Trash2 size={14} />
  </button>

  {/* EXISTING MENU */}
  <button
    onClick={() => setShowModal(true)}
    className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
  >
    <MoreHorizontal size={15} />
  </button>
</div>
 
                {openMenu === sub.id && (
                  <div className="absolute right-0 mt-1 bg-white border rounded shadow-md p-2 text-xs w-52 z-10">
                   
                    <div className="font-semibold mb-1">
                      Set Dependency
                    </div>
 
                    {/* Show current dependency */}
                    {dependency && (
                      <div className="mb-2 text-green-600">
                        Depends on: {dependency.title}
                      </div>
                    )}
 
                    {/* Allow selecting dependency only if none exists */}
                    {!sub.depends_on_subtask_id &&
                      subtasks
                        .filter((s) => s.id !== sub.id)
                        .map((s) => (
                          <div
                            key={s.id}
                            className="cursor-pointer hover:bg-gray-100 p-1 rounded"
                            onClick={() =>
                              addDependency(sub.id, s.id)
                            }
                          >
                            Depends on: {s.title}
                          </div>
                        ))}
 
                    {!sub.depends_on_subtask_id &&
                      subtasks.length <= 1 && (
                        <div className="text-gray-400">
                          No other subtasks available
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
 
 
 
 
 
 
       
      </div>
 
      {/* ===== ADD SUBTASK ===== */}
      <div className="mt-4 flex gap-2 text-black">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add subtask"
          className="h-10 min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
        />
 
 
       
 
        <button
          onClick={addSubtask}
          className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-blue-600 px-4 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
        >
          <Plus size={15} />
          Add
        </button>
      </div>
 
 
 
 
 
      {showModal && (
  // <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center">
  <div className="fixed inset-0 z-[10000] bg-black/40 flex justify-center items-center">
    {/* <div className="bg-white p-4 rounded w-96"> */}
    <div className="w-96 rounded-2xl bg-white p-5 text-black shadow-2xl">
 
      <h3 className="mb-3 flex items-center gap-2 font-black text-slate-800">
        <GitBranch size={17} />
        Create Dependency
      </h3>
 
      {/* DROPDOWN */}
      <select
        className="mb-3 h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
        onChange={(e) => setSelectedParent(e.target.value)}
      >
        <option value="">Select Parent Task</option>
        {subtasks.map((s) => (
          <option key={s.id} value={s.id}>
            {s.title}
          </option>
        ))}
      </select>
 
      {/* CHILD CHECKBOX LIST */}
      <div className="max-h-40 overflow-y-auto mb-3">
        {subtasks.map((s) => (
          <label key={s.id} className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-slate-50">
            <input
              type="checkbox"
              checked={selectedChildren.includes(s.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedChildren([...selectedChildren, s.id]);
                } else {
                  setSelectedChildren(
                    selectedChildren.filter(id => id !== s.id)
                  );
                }
              }}
            />
            {s.title}
          </label>
        ))}
      </div>
 
      {/* BUTTON */}
      <button
        onClick={handleSubmitDependency}
        className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white"
      >
        Submit
      </button>
 
      <button
        onClick={() => setShowModal(false)}
        className="ml-2 rounded-xl px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100"
      >
        Cancel
      </button>
 
    </div>
  </div>
)}
 
 
 {editSubtask && (
  <div className="fixed inset-0 z-[10000] bg-black/40 flex justify-center items-center">
    <div className="w-96 rounded-2xl bg-white p-5 text-black shadow-2xl">

      <h3 className="mb-3 font-black text-slate-800">Edit Subtask</h3>

      <input
        value={editTitle}
        onChange={(e) => setEditTitle(e.target.value)}
        className="mb-3 h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
        placeholder="Subtask title"
      />

      {/* <input
        type="date"
        value={editDate}
        onChange={(e) => setEditDate(e.target.value)}
        className="w-full border p-2 mb-3"
      /> */}

      <button
        onClick={handleUpdateSubtask}
        className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white"
      >
        Update
      </button>

      <button
        onClick={() => setEditSubtask(null)}
        className="ml-2 rounded-xl px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100"
      >
        Cancel
      </button>
    </div>
  </div>
)}
 
    </section>
  );
}
 
 
 
 
//     </div>
//   );
// }
 
 




